import React, { useMemo } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useCommonStore, { ModalConfig } from '@/stores/common';
import { useCreateParticipantMutation } from '@/services';
import { toHHmm } from '@/utils/common';
import { useQueryClient } from '@tanstack/react-query';

/**
 * A11y helper class for visually hidden labels.
 */
const srOnly = 'sr-only';

type ProcessedLessonRow = {
  id?: number;
  index?: number;
  instructor?: { name?: string } | null;
  client_name?: string | null;
  start?: string | number | Date | null;
  lesson_code?: { id?: number; code?: string; color?: string } | null;
  mountain?: { name?: string; id?: number } | null;
  am?: number | null;
  pm?: number | null;
  fd?: number | null;
  level?: string | number | null;
  gross?: string | number;
  commission?: string | number;
  net?: number;
  number_in_party?: number;
};

const headerCell =
  'px-4 py-2 bg-blue-600 text-white text-xs md:text-sm font-semibold text-center border border-white';
const valueCell =
  'px-4 py-3 text-gray-800 text-xs md:text-sm bg-gray-50 border border-gray-200';

// Zod schema for validation
const schema = z.object({
  client_name: z.string().min(1, 'Client name is required'),
  payment_method: z.enum(['cash', 'commission'])
});

type FormData = z.infer<typeof schema>;

const ProcessedLessonDetailsModal: React.FC = () => {
  const queryClient = useQueryClient();
  const { modalConfig = {} as ModalConfig, hideCommonModal } = useCommonStore(
    state => state
  );
  const lesson = (modalConfig?.data?.lesson || {}) as ProcessedLessonRow;
  const { mutateAsync: updateParticipant, isPending: isUpdating } =
    useCreateParticipantMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      client_name: '',
      payment_method: 'commission'
    },
    mode: 'onChange'
  });

  const paymentMethod = watch('payment_method');

  const { gross, commissionValue, net } = useMemo(() => {
    const g = Number(lesson?.gross) || 0;
    const c = paymentMethod === 'cash' ? 0 : Number(lesson?.commission) || 0;
    return { gross: g, commissionValue: c, net: g - c };
  }, [lesson?.gross, lesson?.commission, paymentMethod]);

  const onSubmit = async (data: FormData) => {
    // Build payload based on form inputs and lesson details
    const finalPayload = {
      client_name: data.client_name,
      lesson_code_id: lesson?.lesson_code?.id ?? null,
      number_in_party: 1,
      total_gross: Number(lesson?.gross) || 0,
      commission: commissionValue,
      total_net: net,
      level: Number(lesson?.level) || 0,
      start: toHHmm(lesson?.start),
      mountain_id: lesson?.mountain?.id ?? 0,
      payment_method: data.payment_method
    };

    // Resolve correct id expected by API (assuming lesson.id is participant id)
    const updateId =
      (modalConfig?.data as any)?.lesson?.id ??
      (modalConfig?.data as any)?.lessonId ??
      (lesson?.id as number);

    try {
      // Submit update
      const res = await updateParticipant({ id: updateId, ...finalPayload });
      if (res?.success) {
        await queryClient.invalidateQueries({
          queryKey: ['lesson-entries'],
          exact: false
        });
        await queryClient.invalidateQueries({
          queryKey: ['lesson-schedule'],
          exact: false
        });
      }
      hideCommonModal();
      // Close modal after successful update
    } catch (err) {
      // Handle mutation error if needed
      console.error(err);
    }
  };

  return (
    <div
      className='p-4 md:p-6'
      role='dialog'
      aria-modal='true'
      aria-label='Processed lesson details update'
    >
      <h2 className='text-lg md:text-xl font-semibold text-gray-900 mb-4'>
        Add Participant
      </h2>

      <div className='w-full overflow-x-auto'>
        <div className='min-w-[720px]'>
          <div className='grid grid-cols-12' role='grid' aria-readonly='true'>
            <div className={`col-span-3 ${headerCell}`}>Instructor</div>
            <div className={`col-span-3 ${headerCell}`}>Client name</div>
            <div className={`col-span-3 ${headerCell}`}>Service</div>
            <div className={`col-span-3 ${headerCell}`}># AM</div>

            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.instructor?.name ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              <div>
                <label htmlFor='client_name' className={srOnly}>
                  Client name
                </label>
                <input
                  id='client_name'
                  type='text'
                  className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  placeholder='Enter client name'
                  aria-label='Client name'
                  {...register('client_name')}
                />
              </div>
              {errors.client_name && (
                <p className='text-red-500 text-sm mb-1'>
                  {errors.client_name.message}
                </p>
              )}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.lesson_code?.code ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>{lesson?.am ?? '-'}</div>

            <div className={`col-span-3 ${headerCell}`}># PM</div>
            <div className={`col-span-3 ${headerCell}`}># FD</div>
            <div className={`col-span-3 ${headerCell}`}>Hotel / Agent</div>
            <div className={`col-span-3 ${headerCell}`}>Mountain</div>

            <div className={`col-span-3 ${valueCell}`}>{lesson?.pm ?? '-'}</div>
            <div className={`col-span-3 ${valueCell}`}>{lesson?.fd ?? '-'}</div>
            <div className={`col-span-3 ${valueCell}`}>
              {(lesson as any)?.hotel?.name ??
                (lesson as any)?.agent?.name ??
                '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.mountain?.name ?? 'N/A'}
            </div>

            <div className={`col-span-3 ${headerCell}`}>Time</div>
            <div className={`col-span-3 ${headerCell}`}>CC</div>
            <div className={`col-span-3 ${headerCell}`}>Gross</div>
            <div className={`col-span-3 ${headerCell}`}>Commission</div>

            <div className={`col-span-3 ${valueCell}`}>
              {toHHmm(lesson?.start) ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              <div>
                <label htmlFor='cc_method' className={srOnly}>
                  Payment method
                </label>
                <select
                  id='cc_method'
                  className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  aria-label='Payment method'
                  {...register('payment_method')}
                >
                  <option value='cash'>Cash</option>
                  <option value='commission'>Commission</option>
                </select>
              </div>
            </div>
            <div className={`col-span-3 ${valueCell}`}>{gross}</div>
            <div className={`col-span-3 ${valueCell}`}>{commissionValue}</div>

            <div className={`col-span-3 ${headerCell}`}>Level</div>
            <div className={`col-span-3 ${headerCell}`}>Net</div>
            <div className={`col-span-6 ${headerCell}`}></div>

            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.level ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>{net}</div>
            <div className={`col-span-6 ${valueCell}`}></div>
          </div>
        </div>
      </div>

      <div className='mt-6 flex justify-end gap-3'>
        <button
          type='button'
          className='px-4 py-2 bg-gray-100 text-slate-700 rounded-md hover:bg-gray-200'
          onClick={() => useCommonStore.getState().hideCommonModal()}
          aria-label='Close modal'
        >
          Close
        </button>
        <button
          type='button'
          className='px-4 py-2 bg-gray-100 text-slate-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || isUpdating}
          aria-label='Apply Add Participant'
        >
          {isSubmitting || isUpdating ? 'Adding...' : 'Add Participant'}
        </button>
      </div>
    </div>
  );
};

export default ProcessedLessonDetailsModal;
